import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { goalsAPI, transactionsAPI, brokersAPI, assetsAPI, portfolioAPI } from '../api/client';

const InvestmentContext = createContext(null);

export function InvestmentProvider({ children }) {
  // Goals
  const [goals, setGoals] = useState([]);
  const [goalsLoading, setGoalsLoading] = useState(false);

  // Transactions
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);

  // Brokers
  const [brokers, setBrokers] = useState([]);
  const [brokersLoading, setBrokersLoading] = useState(false);

  // Assets
  const [assets, setAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(false);

  // Portfolio
  const [portfolio, setPortfolio] = useState([]);
  const [portfolioSummary, setPortfolioSummary] = useState(null);
  const [portfolioLoading, setPortfolioLoading] = useState(false);

  const [error, setError] = useState(null);

  // ---- Goals ----
  const fetchGoals = useCallback(async (params = {}) => {
    setGoalsLoading(true);
    try {
      const res = await goalsAPI.list(params);
      setGoals(res.data.results || res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load goals.');
    } finally {
      setGoalsLoading(false);
    }
  }, []);

  const createGoal = useCallback(async (data) => {
    const res = await goalsAPI.create(data);
    setGoals((prev) => [res.data, ...prev]);
    return res.data;
  }, []);

  const updateGoal = useCallback(async (id, data) => {
    const res = await goalsAPI.partialUpdate(id, data);
    setGoals((prev) => prev.map((g) => (g.id === id ? res.data : g)));
    return res.data;
  }, []);

  const deleteGoal = useCallback(async (id) => {
    await goalsAPI.delete(id);
    setGoals((prev) => prev.filter((g) => g.id !== id));
  }, []);

  // ---- Transactions ----
  const fetchTransactions = useCallback(async (params = {}) => {
    setTransactionsLoading(true);
    try {
      const res = await transactionsAPI.list(params);
      setTransactions(res.data.results || res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load transactions.');
    } finally {
      setTransactionsLoading(false);
    }
  }, []);

  const createTransaction = useCallback(async (data) => {
    const res = await transactionsAPI.create(data);
    setTransactions((prev) => [res.data, ...prev]);
    // Refresh portfolio after new transaction
    fetchPortfolio();
    return res.data;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const deleteTransaction = useCallback(async (id) => {
    await transactionsAPI.delete(id);
    setTransactions((prev) => prev.filter((t) => t.id !== id));
    fetchPortfolio();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Brokers ----
  const fetchBrokers = useCallback(async () => {
    setBrokersLoading(true);
    try {
      const res = await brokersAPI.list();
      setBrokers(res.data.results || res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load brokers.');
    } finally {
      setBrokersLoading(false);
    }
  }, []);

  const createBroker = useCallback(async (data) => {
    const res = await brokersAPI.create(data);
    setBrokers((prev) => [...prev, res.data]);
    return res.data;
  }, []);

  const deleteBroker = useCallback(async (id) => {
    await brokersAPI.delete(id);
    setBrokers((prev) => prev.filter((b) => b.id !== id));
  }, []);

  // ---- Assets ----
  const fetchAssets = useCallback(async () => {
    setAssetsLoading(true);
    try {
      const res = await assetsAPI.list();
      setAssets(res.data.results || res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load assets.');
    } finally {
      setAssetsLoading(false);
    }
  }, []);

  const createAsset = useCallback(async (data) => {
    const res = await assetsAPI.create(data);
    setAssets((prev) => [...prev, res.data]);
    return res.data;
  }, []);

  // ---- Portfolio ----
  const fetchPortfolio = useCallback(async () => {
    setPortfolioLoading(true);
    try {
      const [listRes, summaryRes] = await Promise.all([
        portfolioAPI.list(),
        portfolioAPI.getSummary(),
      ]);
      setPortfolio(listRes.data.results || listRes.data);
      setPortfolioSummary(summaryRes.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load portfolio.');
    } finally {
      setPortfolioLoading(false);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  // Initial data load
  useEffect(() => {
    fetchGoals();
    fetchBrokers();
    fetchAssets();
    fetchPortfolio();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const value = {
    // Goals
    goals,
    goalsLoading,
    fetchGoals,
    createGoal,
    updateGoal,
    deleteGoal,

    // Transactions
    transactions,
    transactionsLoading,
    fetchTransactions,
    createTransaction,
    deleteTransaction,

    // Brokers
    brokers,
    brokersLoading,
    fetchBrokers,
    createBroker,
    deleteBroker,

    // Assets
    assets,
    assetsLoading,
    fetchAssets,
    createAsset,

    // Portfolio
    portfolio,
    portfolioSummary,
    portfolioLoading,
    fetchPortfolio,

    error,
    clearError,
  };

  return (
    <InvestmentContext.Provider value={value}>
      {children}
    </InvestmentContext.Provider>
  );
}

export function useInvestmentContext() {
  const ctx = useContext(InvestmentContext);
  if (!ctx) throw new Error('useInvestmentContext must be used within InvestmentProvider');
  return ctx;
}

export default InvestmentContext;
